import InvoiceAdjustmentDetails from '~/components/shared/InvoiceAdjustmentDetails';
import VehicleDetails from '~/components/shared/VehicleDetails';
import RequestDialogs from '~/components/shared/RequestDialogs';
import {
  ScheduleGrouping,
  ScheduleDateRow,
  ScheduleTimeRow,
} from '~/components/shared/ScheduleDetails';
import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import {useNavigation} from '@react-navigation/native';
import moment from 'moment';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import sy from '~/styles';
import withDimensions from '~/components/hoc/with-dimensions';
import {
  Text,
  PrivateText,
  Pressable,
  Dialog,
  FormTextInput,
  Checkbox,
  Calendar,
  MONTHS_3_PAST_6_FUTURE,
  ReplacementIcon,
  ReplacementText,
  RobTaskIcon,
  Link,
  KpiWarning,
  WarrantyIcon,
  LessorPersona,
  ServiceWarning,
  LessorPhone,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {
  Row as GroupingRow,
  EditDialog as GroupingRowEdit,
} from '~/components/controls/3/Grouping';
import {
  MaintenanceActionsheet,
  TaskActionsheet,
  SlaActionsheet,
  TiresActionsheet,
  DealerUserActionsheet,
  ReplacementActionsheet,
} from '~/components/shared/Actionsheets';
import {
  DealerRequestDialog,
  InvoiceUploadDialog,
} from '~/components/shared/Dialogs';
import {CreateDamageModal} from '~/components/shared/Modals';
import Format from '~/lib/format';
import {REQUEST_TYPES} from '~/types/request';
import ProposalButton from './ProposalButton';
import {
  service_request as serviceRequestApi,
  rob as robApi,
  car as carApi,
  fleet as fleetApi,
  dealer as dealerApi,
} from '~/api/private';
import {
  STATUS_DESCRIPTIONS,
  STATUS_CODES,
  STATUS_ORDER,
} from '~/types/statuses';
import {
  isRobTaskReady,
  isRobTaskApproved,
  isRobTaskCancelled,
  isRobTaskChanged,
  isNotInvoiced,
  isArchived,
  isNotReadyForInvoicing,
  ROB_TASK_STATUS,
  ROB_NOT_INVOICED_STATUS_LABELS,
  ROB_READY_CODES,
} from '~/types/rob';
import {SERVICE_TYPE_COMPONENTS_ORDER} from '~/types/services';
import {REPLACEMENT_TYPE} from '~/types/replacement';
import {SOURCES} from '~/types/sources';
import {useDispatch, useSelector} from '~/lib/hooks';
import {setAppDoneLoading, setAppIsLoading} from '~/actions';
import ThumbUpIcon from '~/images/md-icons/thumb_up/materialicons/24px.svg';
import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import ListIcon from '~/images/md-icons/list/materialicons/24px.svg';
import BlockIcon from '~/images/md-icons/block/materialicons/24px.svg';
import ReceiptIcon from '~/images/md-icons/receipt/materialicons/24px.svg';
import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import RefreshIcon from '~/images/md-icons/refresh/materialicons/24px.svg';
import WarningLargeIcon from '~/images/md-icons/warning/materialicons/24px.svg';
import WarningAmberIcon from '~/images/md-icons/warning_amber/materialicons/24px.svg';
import ReceiptLongIcon from '~/images/md-icons/receipt_long/materialicons/24px.svg';
import PublishedWithChangesIcon from '~/images/md-icons/published_with_changes/materialicons/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import FileOpenIcon from '~/images/md-icons/file_open/materialicons/24px.svg';
import PersonIcon from '~/images/md-icons/person/materialicons/24px.svg';
import MoreTimeIcon from '~/images/md-icons/more_time/materialicons/24px.svg';
import TimerIcon from '~/images/md-icons/timer/materialsymbolsoutlined/24px.svg';
import ProductionQuantityLimitsIcon from '~/images/md-icons/production_quantity_limits/materialsymbolsoutlined/24px.svg';
import ReplacementVehicleRow from './ReplacementVehicleRow';
import TaskSubtitle from './TaskSubtitle';
import Footer from './Footer';
import Details from './Details';
import RobReasonsDialog from './RobReasonsDialog';
import ClientFields from './ClientFields';
import PdfViewerLink from './PdfViewerLink';
import ServiceHistoryLink from './ServiceHistoryLink';
import CancelRobTaskDialog from './CancelRobTaskDialog';
import MaintenanceExtrasDialog from './MaintenanceExtrasDialog';
import ServiceRequestContext from '../context';
import {REQUEST_EDIT_SCOPES} from './withServiceRequestContext';
import {INVOICE_DISCOUNT_LABELS, INVOICE_DISCOUNT_ORDER} from '../constants';
const getComponentMainTask = (component) =>
  component?.tasks.find((task) => task.is_main_task);
const getComponentById = (components, id) =>
  components.find((item) => item.id === id);
const isUnorderedComponentWithoutTask = (component, task) =>
  !task && !SERVICE_TYPE_COMPONENTS_ORDER.includes(component.name);
const compareFleetComponents = (component_a, component_b) => {
  const component_a_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
    component_a.name,
  );
  const component_b_index = SERVICE_TYPE_COMPONENTS_ORDER.indexOf(
    component_b.name,
  );
  if (component_a_index < 0 && component_b_index < 0) {
    return null;
  }
  if (component_a_index === -1) {
    return 1;
  }
  if (component_b_index === -1) {
    return -1;
  }
  if (component_a_index !== component_b_index) {
    return component_a_index - component_b_index;
  }
  return null;
};
const compareNonFleetRobCodes = (task_a, task_b) => {
  if (task_a.rob_code && !task_b.rob_code) {
    return -1;
  }
  if (task_b.rob_code && !task_a.rob_code) {
    return 1;
  }
  if (task_a.rob_code && task_b.rob_code) {
    return task_a.rob_code.localeCompare(task_b.rob_code);
  }
  return null;
};
const getTaskSortText = (task) => task.description ?? task.work_text ?? '';
const compareComponentIds = (components, is_fleet_car) => (a, b) => {
  const component_a = getComponentById(components, a);
  const component_b = getComponentById(components, b);
  const task_a = getComponentMainTask(component_a);
  const task_b = getComponentMainTask(component_b);
  if (isUnorderedComponentWithoutTask(component_a, task_a)) {
    return 1;
  }
  if (isUnorderedComponentWithoutTask(component_b, task_b)) {
    return -1;
  }
  if (!task_a && !task_b) {
    return (
      SERVICE_TYPE_COMPONENTS_ORDER.indexOf(component_a.name) -
      SERVICE_TYPE_COMPONENTS_ORDER.indexOf(component_b.name)
    );
  }
  if (!task_a) {
    return -1;
  }
  if (!task_b) {
    return 1;
  }
  if (is_fleet_car) {
    const fleetCompare = compareFleetComponents(component_a, component_b);
    if (fleetCompare != null) {
      return fleetCompare;
    }
  } else {
    const nonFleetCompare = compareNonFleetRobCodes(task_a, task_b);
    if (nonFleetCompare != null) {
      return nonFleetCompare;
    }
  }
  return getTaskSortText(task_a).localeCompare(getTaskSortText(task_b));
};
const shouldIncludeTaskAmount = ({
  task,
  overview,
  is_fleet_car,
  is_retail_car,
}) => {
  if (is_retail_car) {
    return true;
  }
  if (!is_fleet_car && !task.rob_code) {
    return false;
  }
  const robRequest =
    overview?.dealer_request ?? overview?.fleet_request ?? overview?.rob;
  if (isRobTaskCancelled(robRequest?.components[task.id]?.status.code)) {
    return false;
  }
  if (!task.is_main_task) {
    const main_task = overview.components
      .flatMap((c) => c.tasks ?? [])
      .find((item) => item.is_main_task && item.rob_code === task.rob_code);
    if (!main_task) {
      return true;
    }
    if (
      isRobTaskCancelled(robRequest?.components[main_task.id]?.status.code) ||
      main_task.warranty
    ) {
      return false;
    }
  }
  return true;
};
const getRequestStatusDescription = (overview) => {
  if (isNotInvoiced(overview.rob?.invoicing?.status)) {
    return STATUS_DESCRIPTIONS[STATUS_CODES.NoInvoice];
  }
  if (
    overview.status === STATUS_CODES.Done &&
    isNotReadyForInvoicing(overview.rob?.invoicing?.status)
  ) {
    return STATUS_DESCRIPTIONS[STATUS_CODES.NotReadyForInvoicing];
  }
  return STATUS_DESCRIPTIONS[overview.status];
};
const getRequestData = (overview) =>
  overview?.dealer_request ?? overview?.fleet_request ?? overview?.rob;
const getRequestTaskStatusCode = (overview, mainTaskId) =>
  getRequestData(overview)?.components[mainTaskId]?.status.code;
const getRequestTaskStatusFlags = (overview, mainTaskId) => {
  const statusCode = getRequestTaskStatusCode(overview, mainTaskId);
  return {
    statusCode,
    approved: isRobTaskApproved(statusCode),
    cancelled: statusCode === ROB_TASK_STATUS.Cancelled,
    cancellationApproved: statusCode === ROB_TASK_STATUS.CancellationApproved,
    cancellationDenied: statusCode === ROB_TASK_STATUS.CancellationDenied,
  };
};
const isTaskCancellationState = ({
  cancelled,
  cancellationApproved,
  cancellationDenied,
}) => cancelled || cancellationApproved || cancellationDenied;
const getRequestTaskTitle = ({main_task, component}) => {
  if (!main_task?.reference) {
    return component.name;
  }
  return main_task?.source === 'TecRMI' ? main_task.work_text : component.name;
};
const getInvoiceItemSubject = (item) => {
  if (item.lessor) {
    return <>&nbsp;&middot;&nbsp;{item.lessor}</>;
  }
  if (item.subject) {
    return <>&nbsp;&middot;&nbsp;{item.subject}</>;
  }
  return null;
};
const renderRequestRobStatusIcon = ({
  overview,
  main_task,
  robStatusCode,
  is_fleet_car,
  is_retail_car,
  is_task_ready,
  setShowRobReasonsDialog,
}) => {
  if (is_fleet_car || is_retail_car) {
    return (
      <RobTaskIcon
        statuses={[getRequestApprovalStatusCode(overview, main_task?.id)]}
        width={18}
        height={18}
      />
    );
  }
  if (!is_task_ready) {
    return null;
  }
  return (
    <Pressable
      onPress={() =>
        setShowRobReasonsDialog({
          rob_code: main_task?.rob_code,
        })
      }>
      <RobTaskIcon statuses={[robStatusCode]} width={18} height={18} />
    </Pressable>
  );
};
const renderDamageRequestLink = ({
  overview,
  robContract,
  navigation,
  onNewDamageRequest,
}) => {
  if (
    robContract?.supplierContract?.repairAndMaintenance
      .componentsWithDamageAllowed === false
  ) {
    if (overview?.damage_request?.id) {
      return (
        <Link
          onPress={() =>
            navigation.navigate('Damage', {
              damage_id: overview?.damage_request?.id,
            })
          }>
          {overview.damage_request.service_request_number ?? 'Ga naar opdracht'}
        </Link>
      );
    }
    if (robContract?.permissions?.damage) {
      return <Link onPress={onNewDamageRequest}>Nieuwe opdracht</Link>;
    }
    return <Text>Niet toegestaan</Text>;
  }
  return <Text>Toegestaan</Text>;
};
const getRequestApprovalStatusCode = (overview, taskId) =>
  (overview.dealer_request ?? overview.fleet_request)?.components?.[taskId]
    ?.status.code;
const shouldShowRequestRobStatus = ({overview, taskId, robStatusCode}) => {
  const approvalStatusCode = getRequestApprovalStatusCode(overview, taskId);
  return (
    (approvalStatusCode &&
      approvalStatusCode !== ROB_TASK_STATUS.ApprovalRequested) ||
    (overview?.rob && robStatusCode && !isRobTaskChanged(robStatusCode))
  );
};
const renderRequestTaskIndicators = ({
  overview,
  robContract,
  car,
  main_task,
  robStatusCode,
  is_fleet_car,
  is_retail_car,
  is_task_ready,
  has_rob_errors,
  rob_code,
  component,
  has_custom_time,
  has_more_time,
  has_work_time_overlap,
  has_custom_articles,
  has_discount,
  has_markup,
  is_warranty,
  is_damage,
  kpi,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowCustomTimeInfoDialog,
  setShowMoreTimeInfoDialog,
  setShowOverlapInfoDialog,
  setShowCustomArticlesInfoDialog,
  setShowDiscountInfoDialog,
  setShowMarkupInfoDialog,
  robComponents,
}) => (
  <View style={[sy['flex-row'], sy['items-center'], sy['gap-1']]}>
    {has_rob_errors && (
      <Pressable
        onPress={() =>
          setShowRobTaskErrorsDialog({
            title: (
              <Text style={[sy.largePlus]}>
                {rob_code}&nbsp;&middot;&nbsp;
                {robComponents[rob_code]?.description}
              </Text>
            ),
            component_id: component.id,
          })
        }>
        <ErrorIcon width={18} height={18} fill="#972727" />
      </Pressable>
    )}
    {shouldShowRequestRobStatus({
      overview,
      taskId: main_task?.id,
      robStatusCode,
    }) ? (
      renderRequestRobStatusIcon({
        overview,
        main_task,
        robStatusCode,
        is_fleet_car,
        is_retail_car,
        is_task_ready,
        setShowRobReasonsDialog,
      })
    ) : (
      <>
        {has_custom_time && (
          <Pressable onPress={() => setShowCustomTimeInfoDialog(true)}>
            <TimerIcon fill="#FF6C00" width={18} height={18} />
          </Pressable>
        )}
        {!has_custom_time && has_more_time && (
          <Pressable onPress={() => setShowMoreTimeInfoDialog(true)}>
            <MoreTimeIcon fill="#FF6C00" width={18} height={18} />
          </Pressable>
        )}
        {!has_custom_time && !has_more_time && has_work_time_overlap && (
          <Pressable onPress={() => setShowOverlapInfoDialog(true)}>
            <PublishedWithChangesIcon fill="#299976" width={18} height={18} />
          </Pressable>
        )}
        {has_custom_articles && (
          <Pressable onPress={() => setShowCustomArticlesInfoDialog(true)}>
            <ProductionQuantityLimitsIcon
              fill="#FF6C00"
              width={18}
              height={18}
            />
          </Pressable>
        )}
        {has_discount && (
          <Pressable onPress={() => setShowDiscountInfoDialog(true)}>
            <DiscountIcon fill="#299976" width={18} height={18} />
          </Pressable>
        )}
        {has_markup && (
          <Pressable onPress={() => setShowMarkupInfoDialog(true)}>
            <SellIcon fill="#FF6C00" width={18} height={18} />
          </Pressable>
        )}
        {(is_warranty || is_damage) && (
          <WarrantyIcon
            value={{
              warranty: main_task.warranty,
              is_damage: main_task.is_damage,
            }}
            width={18}
            height={18}
          />
        )}
        {component.name === 'Onderhoud' && (
          <ServiceWarning
            service={{
              last:
                robContract?.last_service_request?.maintenance?.date ??
                robContract?.vehicle?.maintenance?.lastMaintenanceDate,
              kilometrage: isNil(overview.mileage)
                ? null
                : overview.mileage -
                  (robContract?.last_service_request?.maintenance?.mileage ??
                    0),
              ascription: car?.entry_date,
            }}
          />
        )}
        {kpi && (
          <KpiWarning
            style={[sy['flex-1'], sy['items-end'], sy['justify-end']]}
            kpi={kpi}
          />
        )}
      </>
    )}
  </View>
);
const getRequestMainTasks = (component) => {
  const mainTasks = component.tasks.filter((item) => item.is_main_task);
  if (!mainTasks.length) {
    return [null];
  }
  return mainTasks.sort((a, b) => {
    if (a?.rob_code === b?.rob_code) {
      return 0;
    }
    return a?.rob_code < b?.rob_code ? -1 : 1;
  });
};
const getRequestTaskMeta = ({
  overview,
  main_task,
  tasks,
  robContract,
  is_fleet_car,
  is_retail_car,
  slaKpis,
}) => {
  const rob_code = main_task?.rob_code;
  const operation_code = main_task?.operation;
  const amount = tasks
    .filter((task) => task.rob_code === rob_code)
    .reduce((acc, task) => acc + task.task_amount, 0);
  const task_ready = isRobTaskReady(
    robContract,
    main_task,
    amount,
    getRequestData(overview)?.components[main_task?.id],
    is_fleet_car,
    is_retail_car,
  );
  const is_task_ready = task_ready === ROB_READY_CODES.Ready;
  const is_sla_item = tasks.length
    ? tasks.some((item) => isFinite(item.amount))
    : false;
  const {
    statusCode,
    approved,
    cancelled,
    cancellationApproved,
    cancellationDenied,
  } = getRequestTaskStatusFlags(overview, main_task?.id);
  const robStatusCode = overview?.rob?.components[main_task?.id]?.status.code;
  const isCancelledState = isTaskCancellationState({
    cancelled,
    cancellationApproved,
    cancellationDenied,
  });
  const has_discount = main_task?.task_discount > 0;
  const has_markup = main_task?.task_markup > 0;
  const is_damage = main_task?.is_damage;
  const is_warranty = !isNil(main_task?.warranty);
  const has_custom_time =
    !is_sla_item && main_task?.original_work_time === null;
  const has_custom_articles = main_task?.parts?.some(
    (item) => item.source === SOURCES.Custom,
  );
  const has_more_time =
    !is_sla_item &&
    !isNil(main_task?.reference) &&
    tasks.reduce((acc, item) => acc + (item.duration ?? 0), 0) >
      tasks.reduce(
        (acc, item) =>
          acc +
          (isFinite(item.calculated_work_time)
            ? item.calculated_work_time
            : item.original_work_time),
        0,
      );
  const has_work_time_overlap = tasks.some(
    (task) =>
      isFinite(task.calculated_work_time) && task.original_duration === null,
  );
  const kpi = slaKpis?.[main_task?.rob_code];
  return {
    rob_code,
    operation_code,
    amount,
    task_ready,
    is_task_ready,
    is_sla_item,
    statusCode,
    approved,
    cancelled,
    cancellationApproved,
    cancellationDenied,
    robStatusCode,
    isCancelledState,
    has_discount,
    has_markup,
    is_damage,
    is_warranty,
    has_custom_time,
    has_custom_articles,
    has_more_time,
    has_work_time_overlap,
    kpi,
  };
};
const openRequestTaskEditor = ({
  component,
  is_sla_item,
  main_task,
  tasks,
  main_tasks,
  setShowMaintenanceEdit,
  setShowTiresEdit,
  setShowSlaEdit,
  setShowTaskEdit,
}) => {
  if (component.name === 'Onderhoud' && !is_sla_item) {
    let reference = null;
    if (main_task?.reference) {
      reference =
        main_task?.source === 'TecRMI'
          ? Number.parseInt(main_task?.reference)
          : main_task?.reference;
    }
    setShowMaintenanceEdit({
      task: {
        ...main_task,
        reference,
        parts:
          main_task?.parts?.reduce((acc, item) => {
            acc[item.attributes.itemMpId] = item;
            return acc;
          }, {}) ?? {},
      },
      additional: tasks.filter((item) => item.type !== 'MAINTENANCE'),
      component,
    });
    return;
  }
  if (component.name === 'Banden') {
    setShowTiresEdit({
      tasks: main_tasks,
    });
    return;
  }
  if (is_sla_item) {
    setShowSlaEdit({
      task: tasks[0],
      component,
    });
    return;
  }
  setShowTaskEdit({
    main_task,
    parts: [...(main_task?.parts ?? []), ...(component.parts ?? [])],
    tasks,
    component,
  });
};
const handleRequestCheckboxPress = ({
  isCancelledState,
  component,
  main_task,
  onActivateComponent,
  user_settings,
  overview,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
}) => {
  if (isCancelledState) {
    onActivateComponent(component.id, main_task?.id);
  } else if (
    !user_settings.never_show_rob_task_cancel_dialog &&
    overview?.rob?.components[main_task?.id]
  ) {
    setShowCancelRobTaskDialog(main_task);
  } else {
    onDeleteComponent(component.id, main_task?.id);
  }
  return true;
};
const renderRequestTaskRowReadOnly = ({
  approved,
  is_fleet_car,
  is_retail_car,
  rob_code,
  robComponents,
  main_task,
  component,
  amount,
}) => {
  if (!approved) {
    return <></>;
  }
  return (
    <>
      <View
        style={[sy['flex-row'], sy['p-4'], sy['justify-between'], sy['gap-4']]}>
        <View style={[sy['flex-1'], sy['flex-row']]}>
          {is_fleet_car || is_retail_car ? (
            <></>
          ) : (
            <Text>{rob_code}&nbsp;&middot;&nbsp;</Text>
          )}
          <Text>
            {robComponents[rob_code]?.description ??
              main_task?.description ??
              component.name}
          </Text>
        </View>
        <Text style={sy.smallRegular}>{Format.price(amount)}</Text>
      </View>
      <View style={[sy['px-4']]}>
        <Divider style={sy['bg-gray95']} />
      </View>
    </>
  );
};
const renderRequestTaskRowEditable = ({
  isCancelledState,
  component,
  main_task,
  onActivateComponent,
  user_settings,
  overview,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
  editable,
  cancelled,
  cancellationDenied,
  cancellationApproved,
  onTaskPress,
  rob_code,
  is_fleet_car,
  is_retail_car,
  robComponents,
  tasks,
  is_sla_item,
  slaOperations,
  operation_code,
  is_task_ready,
  amount,
  robStatusCode,
  has_rob_errors,
  has_custom_time,
  has_more_time,
  has_work_time_overlap,
  has_custom_articles,
  has_discount,
  has_markup,
  is_warranty,
  is_damage,
  kpi,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowCustomTimeInfoDialog,
  setShowMoreTimeInfoDialog,
  setShowOverlapInfoDialog,
  setShowCustomArticlesInfoDialog,
  setShowDiscountInfoDialog,
  setShowMarkupInfoDialog,
  robContract,
  car,
}) => (
  <>
    <View
      style={[
        sy['flex-row'],
        sy['flex-1'],
        sy['p-4'],
        sy['gap-4'],
        sy['items-center'],
        !editable && sy['view-disabled'],
      ]}>
      <Checkbox
        style={[
          sy['justify-start'],
          {
            width: 'auto',
          },
        ]}
        disabled={!editable}
        checked={!cancelled && !cancellationDenied && !cancellationApproved}
        onPress={() =>
          handleRequestCheckboxPress({
            isCancelledState,
            component,
            main_task,
            onActivateComponent,
            user_settings,
            overview,
            setShowCancelRobTaskDialog,
            onDeleteComponent,
          })
        }
      />
      <View style={[sy['flex-1']]}>
        <Pressable
          disabled={
            !component ||
            !editable ||
            cancelled ||
            cancellationApproved ||
            cancellationDenied
          }
          onPress={onTaskPress}>
          <View style={[sy['flex-row'], sy['justify-between']]}>
            <View style={[sy['flex-1']]}>
              <View style={[sy['flex-row']]}>
                {rob_code ? (
                  <Text
                    style={[
                      (cancelled ||
                        cancellationApproved ||
                        cancellationDenied) &&
                        sy['line-through'],
                      sy.truncate,
                    ]}>
                    {is_fleet_car || is_retail_car ? (
                      main_task?.description ?? component.name
                    ) : (
                      <>
                        {rob_code}&nbsp;&middot;&nbsp;
                        {robComponents[rob_code]?.description ?? component.name}
                      </>
                    )}
                  </Text>
                ) : (
                  <Text
                    style={[
                      (cancelled ||
                        cancellationApproved ||
                        cancellationDenied) &&
                        sy['line-through'],
                      sy.truncate,
                    ]}>
                    {getRequestTaskTitle({
                      main_task,
                      component,
                    })}
                  </Text>
                )}
              </View>
              {!!component && (
                <TaskSubtitle
                  lessor={overview?.lessor}
                  tasks={tasks}
                  is_maintenance_item={component.name === 'Onderhoud'}
                  is_tires_item={component.name === 'Banden'}
                  is_sla_item={
                    is_sla_item ||
                    main_task?.type === 'SLA_TASK_DURATION' ||
                    slaOperations?.[rob_code]?.some(
                      (x) => x.operation === operation_code,
                    )
                  }
                  ready={is_task_ready}
                  enabled={editable && !cancelled}
                  cancelled={cancelled}
                  cancellation_approved={cancellationApproved}
                  cancellation_denied={cancellationDenied}
                  rob_components={robComponents}
                  is_fleet_car={is_fleet_car}
                  is_retail_car={is_retail_car}
                />
              )}
            </View>
          </View>
        </Pressable>
      </View>
      {component && (
        <View style={[sy['items-end']]}>
          {is_task_ready ? (
            <Text style={sy.smallRegular}>
              {Format.price(
                cancelled || cancellationApproved || cancellationDenied
                  ? 0
                  : amount,
              )}
            </Text>
          ) : (
            <Pressable disabled={!editable && !cancelled} onPress={onTaskPress}>
              <ListIcon />
            </Pressable>
          )}
          {renderRequestTaskIndicators({
            overview,
            rob_request: getRequestData(overview),
            main_task,
            robStatusCode,
            is_fleet_car,
            is_retail_car,
            is_task_ready,
            has_rob_errors,
            rob_code,
            component,
            has_custom_time,
            has_more_time,
            has_work_time_overlap,
            has_custom_articles,
            has_discount,
            has_markup,
            is_warranty,
            is_damage,
            kpi,
            setShowRobTaskErrorsDialog,
            setShowRobReasonsDialog,
            setShowCustomTimeInfoDialog,
            setShowMoreTimeInfoDialog,
            setShowOverlapInfoDialog,
            setShowCustomArticlesInfoDialog,
            setShowDiscountInfoDialog,
            setShowMarkupInfoDialog,
            robComponents,
            robContract,
            car,
          })}
        </View>
      )}
    </View>
    <View style={[sy['px-4']]}>
      <Divider style={sy['bg-gray95']} />
    </View>
  </>
);
const renderRequestTaskRow = ({
  key,
  read_only,
  approved,
  is_fleet_car,
  is_retail_car,
  rob_code,
  robComponents,
  main_task,
  component,
  amount,
  editable,
  cancelled,
  cancellationDenied,
  cancellationApproved,
  isCancelledState,
  onActivateComponent,
  user_settings,
  overview,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
  onTaskPress,
  tasks,
  is_sla_item,
  slaOperations,
  operation_code,
  is_task_ready,
  statusCode,
  robStatusCode,
  has_rob_errors,
  has_custom_time,
  has_more_time,
  has_work_time_overlap,
  has_custom_articles,
  has_discount,
  has_markup,
  is_warranty,
  is_damage,
  kpi,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowCustomTimeInfoDialog,
  setShowMoreTimeInfoDialog,
  setShowOverlapInfoDialog,
  setShowCustomArticlesInfoDialog,
  setShowDiscountInfoDialog,
  setShowMarkupInfoDialog,
  robContract,
  car,
}) => (
  <React.Fragment key={key}>
    {read_only
      ? renderRequestTaskRowReadOnly({
          approved,
          is_fleet_car,
          is_retail_car,
          rob_code,
          robComponents,
          main_task,
          component,
          amount,
        })
      : renderRequestTaskRowEditable({
          isCancelledState,
          component,
          main_task,
          onActivateComponent,
          user_settings,
          overview,
          setShowCancelRobTaskDialog,
          onDeleteComponent,
          editable,
          cancelled,
          cancellationDenied,
          cancellationApproved,
          onTaskPress,
          rob_code,
          is_fleet_car,
          is_retail_car,
          robComponents,
          tasks,
          is_sla_item,
          slaOperations,
          operation_code,
          is_task_ready,
          amount,
          robStatusCode,
          has_rob_errors,
          has_custom_time,
          has_more_time,
          has_work_time_overlap,
          has_custom_articles,
          has_discount,
          has_markup,
          is_warranty,
          is_damage,
          kpi,
          setShowRobTaskErrorsDialog,
          setShowRobReasonsDialog,
          setShowCustomTimeInfoDialog,
          setShowMoreTimeInfoDialog,
          setShowOverlapInfoDialog,
          setShowCustomArticlesInfoDialog,
          setShowDiscountInfoDialog,
          setShowMarkupInfoDialog,
          robContract,
          car,
        })}
  </React.Fragment>
);
const renderRequestComponentRows = ({
  components,
  overview,
  robErrors,
  robContract,
  is_fleet_car,
  is_retail_car,
  slaKpis,
  setShowMaintenanceEdit,
  setShowTiresEdit,
  setShowSlaEdit,
  setShowTaskEdit,
  read_only,
  robComponents,
  editable,
  onActivateComponent,
  user_settings,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
  slaOperations,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowCustomTimeInfoDialog,
  setShowMoreTimeInfoDialog,
  setShowOverlapInfoDialog,
  setShowCustomArticlesInfoDialog,
  setShowDiscountInfoDialog,
  setShowMarkupInfoDialog,
  car,
}) =>
  components?.flatMap((component_id) => {
    const component = overview?.components.find(
      (item) => item.id === component_id,
    );
    const has_rob_errors = robErrors
      .map((item) => item.component_id)
      .includes(component?.id);
    const main_tasks = getRequestMainTasks(component);
    return main_tasks.map((main_task) => {
      const tasks = component.tasks.filter(
        (item) => item.rob_code === main_task?.rob_code,
      );
      const {
        rob_code,
        operation_code,
        amount,
        is_task_ready,
        is_sla_item,
        statusCode,
        approved,
        cancelled,
        cancellationApproved,
        cancellationDenied,
        robStatusCode,
        isCancelledState,
        has_discount,
        has_markup,
        is_damage,
        is_warranty,
        has_custom_time,
        has_custom_articles,
        has_more_time,
        has_work_time_overlap,
        kpi,
      } = getRequestTaskMeta({
        overview,
        main_task,
        tasks,
        robContract,
        is_fleet_car,
        is_retail_car,
        slaKpis,
      });
      const onTaskPress = () =>
        openRequestTaskEditor({
          component,
          is_sla_item,
          main_task,
          tasks,
          main_tasks,
          setShowMaintenanceEdit,
          setShowTiresEdit,
          setShowSlaEdit,
          setShowTaskEdit,
        });
      return renderRequestTaskRow({
        key: main_task?.id ?? component_id,
        read_only,
        approved,
        is_fleet_car,
        is_retail_car,
        rob_code,
        robComponents,
        main_task,
        component,
        amount,
        editable,
        cancelled,
        cancellationDenied,
        cancellationApproved,
        isCancelledState,
        onActivateComponent,
        user_settings,
        overview,
        setShowCancelRobTaskDialog,
        onDeleteComponent,
        onTaskPress,
        tasks,
        is_sla_item,
        slaOperations,
        operation_code,
        is_task_ready,
        statusCode,
        robStatusCode,
        has_rob_errors,
        has_custom_time,
        has_more_time,
        has_work_time_overlap,
        has_custom_articles,
        has_discount,
        has_markup,
        is_warranty,
        is_damage,
        kpi,
        setShowRobTaskErrorsDialog,
        setShowRobReasonsDialog,
        setShowCustomTimeInfoDialog,
        setShowMoreTimeInfoDialog,
        setShowOverlapInfoDialog,
        setShowCustomArticlesInfoDialog,
        setShowDiscountInfoDialog,
        setShowMarkupInfoDialog,
        robContract,
        car,
      });
    });
  });
const shouldShowMaintenanceExtrasDialog = ({
  is_retail_car,
  is_fleet_car,
  overview,
  user_settings,
  hasSeenAddTasks,
}) => {
  let missingRequest = false;
  if (is_retail_car) {
    missingRequest = !overview.dealer_request;
  } else if (is_fleet_car) {
    missingRequest = !overview.fleet_request;
  } else {
    missingRequest = !overview.rob;
  }
  const hasMaintenanceComponent = overview.components.some(
    (item) => item.name === 'Onderhoud',
  );
  const hasSelectedMaintenanceTask = overview.components
    .flatMap((c) => c.tasks ?? [])
    .some(
      (item) =>
        item.type === 'MAINTENANCE' && item.is_main_task && !!item.reference,
    );
  return (
    missingRequest &&
    hasMaintenanceComponent &&
    hasSelectedMaintenanceTask &&
    !user_settings?.never_show_maintenance_extras_dialog &&
    !hasSeenAddTasks
  );
};
const renderRequestScheduleGrouping = ({
  overview,
  editable,
  is_fleet_car,
  editDateTime,
  setEditDateTime,
  onUpdateRequest,
}) => (
  <ScheduleGrouping overview={overview}>
    <ScheduleDateRow
      overview={overview}
      editable={editable}
      editDateTime={editDateTime}
      setEditDateTime={setEditDateTime}
      onUpdateRequest={onUpdateRequest}
    />
    <ScheduleTimeRow
      overview={overview}
      editable={editable}
      editDateTime={editDateTime}
      setEditDateTime={setEditDateTime}
      onUpdateRequest={onUpdateRequest}
    />
    {overview?.status &&
      STATUS_ORDER.indexOf(overview.status) >=
        STATUS_ORDER.indexOf(STATUS_CODES.Done) && (
        <GroupingRow title="Gereed">
          <Text>
            {moment(overview?.completion_date, 'YYYY-MM-DD').format(
              'D MMMM YYYY',
            )}
          </Text>
        </GroupingRow>
      )}
    {overview?.status &&
      STATUS_ORDER.indexOf(overview.status) <
        STATUS_ORDER.indexOf(STATUS_CODES.Done) && (
        <GroupingRow title="Gereed (verwacht)">
          <Text>
            {moment(overview?.expected_completion_date, 'YYYY-MM-DD').format(
              'D MMMM YYYY',
            )}
          </Text>
          {editable && (
            <GroupingRowEdit
              title="Datum wijzigen"
              dismissable
              cancelButton
              disableOKButton={!editDateTime}
              onClose={() => setEditDateTime(null)}
              onOK={() => {
                onUpdateRequest({
                  expected_completion_date: editDateTime,
                });
              }}>
              <Calendar
                months={MONTHS_3_PAST_6_FUTURE}
                initialDate={overview?.expected_completion_date}
                minDate={overview.datetime}
                date={editDateTime ?? overview?.expected_completion_date}
                onPress={setEditDateTime}
              />
            </GroupingRowEdit>
          )}
        </GroupingRow>
      )}
    {((is_fleet_car && overview.fleet_request) || overview.rob) && (
      <GroupingRow title="Opdrachtnummer">
        <Text>
          {overview.fleet_request?.approval_number ??
            overview.rob?.service_request_number ??
            '-'}
        </Text>
      </GroupingRow>
    )}
  </ScheduleGrouping>
);
const renderInvoiceBreakdownRows = ({invoice, total_amount, dimensions}) => {
  if (!invoice?.components) {
    return null;
  }
  return (
    <>
      <GroupingRow
        title={
          <View>
            <Text>Opdracht</Text>
            <Text style={[sy.smallRegular, sy['text-lightgray']]}>
              Exclusief BTW
            </Text>
          </View>
        }>
        <Text style={sy.smallRegular}>
          {Format.price(invoice.amount_approved)}
        </Text>
      </GroupingRow>
      {Format.round(
        invoice.amount_approved -
          (invoice?.amount_approved_discount ?? 0) -
          (invoice?.amount ?? total_amount),
      ) > 0 && (
        <GroupingRow title="Inhouding">
          <Text style={sy.smallRegular}>
            {Format.price(
              invoice.amount_approved -
                (invoice?.amount_approved_discount ?? 0) -
                (invoice?.amount ?? total_amount),
            )}
          </Text>
          <InvoiceAdjustmentDetails title="Inhouding" dimensions={dimensions}>
            {Object.keys(invoice.components)
              .filter((type) => invoice.components[type].amount > 0)
              .sort(
                (a, b) =>
                  INVOICE_DISCOUNT_ORDER.indexOf(a) -
                  INVOICE_DISCOUNT_ORDER.indexOf(b),
              )
              .flatMap((type) => {
                if (
                  [
                    'DEALER_TIRE_SERVICE_PERCENTAGE',
                    'DEALER_TIRE_EXPENSE_PERCENTAGE',
                  ].includes(type)
                ) {
                  return null;
                }
                const item = invoice.components[type];
                let amount = item.amount;
                if (item.type === 'DEALER_SERVICE_PERCENTAGE') {
                  amount +=
                    invoice.components['DEALER_TIRE_SERVICE_PERCENTAGE']
                      ?.amount ?? 0;
                } else if (item.type === 'DEALER_EXPENSE_PERCENTAGE') {
                  amount +=
                    invoice.components['DEALER_TIRE_EXPENSE_PERCENTAGE']
                      ?.amount ?? 0;
                }
                const render_items = item.subcomponents
                  ? item.subcomponents.map((sub_item) => ({
                      ...item,
                      ...sub_item,
                    }))
                  : [
                      {
                        ...item,
                        amount,
                      },
                    ];
                return render_items.map((render_item, index) => (
                  <GroupingRow
                    key={`${type}-${index}`}
                    title={
                      <View>
                        <Text>
                          {INVOICE_DISCOUNT_LABELS[item.type] ?? (
                            <>
                              Korting&nbsp;&middot;&nbsp;
                              {item.subject}
                              {render_item.tire_make ? (
                                <>&nbsp;&middot;&nbsp;{render_item.tire_make}</>
                              ) : null}
                            </>
                          )}
                        </Text>
                        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                          <>
                            {[
                              'DEALER_FEE_AMOUNT',
                              'DEALER_TECRMI_COST',
                              'DEALER_SMS_COST',
                            ].includes(item.type) ? (
                              Format.price(item.amount)
                            ) : (
                              <>
                                {Format.number(
                                  render_item.discount * 100,
                                  1,
                                  false,
                                  {
                                    minimumFractionDigits: 0,
                                  },
                                )}
                                %
                              </>
                            )}
                          </>
                          {getInvoiceItemSubject(item)}
                        </Text>
                      </View>
                    }>
                    <Text style={sy.smallRegular}>
                      {Format.price(render_item.amount)}
                    </Text>
                  </GroupingRow>
                ));
              })}
          </InvoiceAdjustmentDetails>
        </GroupingRow>
      )}
      {invoice?.amount_approved_discount > 0 && (
        <GroupingRow title="Korting">
          <Text style={sy.smallRegular}>
            {Format.price(invoice?.amount_approved_discount)}
          </Text>
          <InvoiceAdjustmentDetails title="Korting" dimensions={dimensions}>
            {Object.keys(invoice.components)
              .filter(
                (type) =>
                  [
                    'SLA_APPROVED_INVOICE_DISCOUNT',
                    'SLA_APPROVED_INVOICE_NEW_TIRES_DISCOUNT',
                  ].includes(type) && invoice.components[type].amount > 0,
              )
              .sort(
                (a, b) =>
                  INVOICE_DISCOUNT_ORDER.indexOf(a) -
                  INVOICE_DISCOUNT_ORDER.indexOf(b),
              )
              .map((type) => {
                const item = invoice.components[type];
                return (
                  <GroupingRow
                    key={type}
                    title={
                      <View>
                        <Text>
                          {INVOICE_DISCOUNT_LABELS[item.type] ?? <>Korting</>}
                        </Text>
                        <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                          <>
                            {Format.number(item.discount * 100, 1, false, {
                              minimumFractionDigits: 0,
                            })}
                            %
                          </>
                          {getInvoiceItemSubject(item)}
                        </Text>
                      </View>
                    }>
                    <Text style={sy.smallRegular}>
                      {Format.price(item.amount)}
                    </Text>
                  </GroupingRow>
                );
              })}
          </InvoiceAdjustmentDetails>
        </GroupingRow>
      )}
    </>
  );
};
const renderInvoiceStatusRow = ({
  overview,
  invoice,
  requestOverview,
  invoiceStatus,
  editInvoiceNumber,
  onUpdateRequest,
  setEditInvoiceNumber,
}) => {
  if (isArchived(invoiceStatus) || requestOverview?.status === 'Invoiced') {
    return null;
  }
  return (
    <GroupingRow
      linkStyle={[
        {
          color:
            !overview.invoice_number && !invoice?.bahnkick_can_invoice
              ? '#FF6C00'
              : null,
        },
      ]}
      title={
        <View>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text>Nummer</Text>
          </View>
          {!overview.invoice_number &&
            !invoice?.bahnkick_can_invoice &&
            invoice?.pdf_upload && (
              <View>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                  Let op: facturatie aan{' '}
                  <>{overview.fleet_administrator ?? overview.lessor}</>
                </Text>
              </View>
            )}
        </View>
      }
      onEditClicked={
        invoice?.bahnkick_can_invoice
          ? null
          : () =>
              setEditInvoiceNumber({
                value: overview?.invoice_number,
                valid: true,
              })
      }>
      <>
        {invoice?.bahnkick_can_invoice && !overview.invoice_number ? (
          <Text style={sy.smallRegular}>(Wordt gegenereerd)</Text>
        ) : (
          overview.invoice_number ?? <>Opgeven</>
        )}
      </>
      {invoice?.bahnkick_can_invoice ? null : (
        <GroupingRowEdit
          title="Factuur"
          dismissable
          cancelButton
          onValidate={async () => {
            if (editInvoiceNumber.value?.length > 0) {
              const result = await onUpdateRequest({
                invoice_number: editInvoiceNumber.value?.trim(),
              });
              if (result) {
                return true;
              }
              setEditInvoiceNumber((prevState) => ({
                ...prevState,
                valid: false,
              }));
              return false;
            }
          }}>
          <View style={[sy['p-4']]}>
            <FormTextInput
              label="Nummer"
              value={editInvoiceNumber?.value}
              hasError={editInvoiceNumber?.valid === false}
              errorMessage="Factuurnummer opslaan is mislukt. Controleer het factuurnummer en probeer het opnieuw."
              onEndEditing={(value) => {
                setEditInvoiceNumber((prevState) => ({
                  ...prevState,
                  value,
                  valid: true,
                }));
              }}
            />
          </View>
        </GroupingRowEdit>
      )}
    </GroupingRow>
  );
};
const renderRequestInvoicePanel = ({
  overview,
  is_fleet_car,
  is_retail_car,
  invoice,
  total_amount,
  editInvoiceNumber,
  setEditInvoiceNumber,
  onUpdateRequest,
  dimensions,
  request_id,
  setShowNoInvoiceDialog,
  onDoRequest,
}) => {
  const requestOverview =
    overview.dealer_request ?? overview.fleet_request ?? overview.rob;
  const invoiceStatus =
    is_fleet_car || is_retail_car ? overview.status : overview.rob?.status;
  const canShowInvoicePanel =
    [STATUS_CODES.Done, STATUS_CODES.DoneNoInvoice].includes(overview.status) ||
    requestOverview?.invoicing?.status === STATUS_CODES.Invoiced;
  if (!canShowInvoicePanel) {
    return null;
  }
  return (
    <View style={[sy['bg-white'], sy['rounded-lg'], sy['overflow-hidden']]}>
      <View
        style={[
          sy['flex-row'],
          sy['items-center'],
          sy['justify-between'],
          sy['p-4'],
        ]}>
        <View style={[sy['flex-row'], sy['flex-1'], sy['gap-4']]}>
          <ReceiptIcon
            fill={
              !isNotInvoiced(requestOverview?.invoicing?.status) &&
              !overview.invoice_number &&
              !invoice?.bahnkick_can_invoice
                ? '#FF6C00'
                : '#4A4A49'
            }
          />
          <Text style={sy.mediumPlus}>
            {isNotInvoiced(requestOverview?.invoicing?.status) ? (
              <>Geen factuur</>
            ) : (
              <>
                Factuur
                {overview.invoice_number && (
                  <>&nbsp;&middot;&nbsp;{overview.invoice_number}</>
                )}
              </>
            )}
          </Text>
        </View>
        {isNotInvoiced(requestOverview?.invoicing?.status) ? (
          <></>
        ) : (
          <Text style={sy.mediumPlus}>
            {Format.price(invoice?.amount ?? total_amount)}
          </Text>
        )}
      </View>
      <Divider style={sy['bg-gray88']} />
      <View style={[sy['bg-white']]}>
        {isNotInvoiced(requestOverview?.invoicing?.status) ? (
          <GroupingRow title="Reden">
            <Text>
              {
                ROB_NOT_INVOICED_STATUS_LABELS[
                  requestOverview?.invoicing?.status
                ]
              }
            </Text>
          </GroupingRow>
        ) : (
          renderInvoiceStatusRow({
            overview,
            invoice,
            requestOverview,
            invoiceStatus,
            editInvoiceNumber,
            onUpdateRequest,
            setEditInvoiceNumber,
          })
        )}

        {!isNotInvoiced(requestOverview?.invoicing?.status) &&
          renderInvoiceBreakdownRows({
            invoice,
            total_amount,
            dimensions,
          })}

        <GroupingRow
          title={
            <View>
              <Text>Totaal</Text>
              <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                Exclusief BTW
              </Text>
            </View>
          }>
          <Text style={sy.smallRegular}>
            {Format.price(invoice?.amount ?? total_amount)}
          </Text>
        </GroupingRow>

        {(invoice?.bahnkick_can_invoice || invoice?.pdf_available) &&
          overview.invoice_number && (
            <GroupingRow
              title={
                <PdfViewerLink
                  invoice={invoice}
                  request_id={request_id}
                  is_fleet_car={is_fleet_car}
                />
              }>
              <FileOpenIcon />
            </GroupingRow>
          )}

        {isArchived(overview.rob?.status) ? (
          <></>
        ) : (
          <>
            {overview.rob?.invoicing?.status === 'ReadyForInvoicing' && (
              <View style={[sy['p-4'], sy['flex-row']]}>
                <BlockIcon />
                <Link
                  style={[sy['pl-8']]}
                  onPress={() => setShowNoInvoiceDialog(true)}>
                  Niet factureren
                </Link>
              </View>
            )}
            {isNotInvoiced(overview.rob?.invoicing?.status) && (
              <View style={[sy['p-4'], sy['flex-row']]}>
                <ReceiptIcon />
                <Link style={[sy['pl-8']]} onPress={async () => onDoRequest()}>
                  Wel factureren
                </Link>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
};
const renderRequestReplacementSection = ({
  overview,
  is_retail_car,
  read_only,
  is_fleet_car,
  robComponents,
  editable,
  setShowReplacementActionsheet,
  setShowRobReasonsDialog,
}) => {
  if (
    !(is_retail_car || overview?.replacement?.type !== REPLACEMENT_TYPE.car)
  ) {
    return null;
  }
  if (read_only) {
    return (
      overview?.replacement?.sla?.rob_code &&
      isRobTaskApproved(
        Object.values(
          (overview?.dealer_request ?? overview?.fleet_request ?? overview?.rob)
            ?.components ?? {},
        ).find((item) => item.rob.code === overview?.replacement.sla.rob_code)
          ?.status.code,
      ) && (
        <>
          <View style={[sy['flex-row'], sy['p-4'], sy['justify-between']]}>
            <View style={[sy['flex-row']]}>
              {!is_fleet_car && !is_retail_car && (
                <Text>
                  {overview?.replacement.sla.rob_code}
                  &nbsp;&middot;&nbsp;
                </Text>
              )}
              <Text>
                {is_fleet_car || is_retail_car
                  ? Object.values(
                      overview.dealer_request?.components ??
                        overview.fleet_request?.components,
                    ).find(
                      (item) =>
                        item.rob.code === overview?.replacement?.sla?.rob_code,
                    )?.description
                  : robComponents[overview.replacement.sla.rob_code]
                      ?.description}
              </Text>
            </View>
            <Text style={sy.smallRegular}>
              {Format.price(overview?.replacement?.sla?.amount)}
            </Text>
          </View>
          <View style={[sy['px-4']]}>
            <Divider style={sy['bg-gray95']} />
          </View>
        </>
      )
    );
  }
  return (
    <View
      style={[
        sy['flex-row'],
        sy['p-4'],
        sy['gap-4'],
        sy['items-center'],
        !editable && sy['view-disabled'],
      ]}>
      <ReplacementIcon type={overview?.replacement?.type} fill="#4A4A49" />
      <View style={[sy['flex-row'], sy['flex-1'], sy['justify-between']]}>
        <Pressable
          disabled={!editable}
          onPress={() => setShowReplacementActionsheet(true)}>
          {!is_fleet_car &&
          !is_retail_car &&
          overview?.replacement?.sla?.rob_code ? (
            <Text>
              {overview.replacement.sla.rob_code}
              &nbsp;&middot;&nbsp;
              {robComponents[overview.replacement.sla.rob_code]?.description}
            </Text>
          ) : (
            <Text>Vervangend vervoer</Text>
          )}

          {overview?.replacement?.type ? (
            <ReplacementText
              type={overview?.replacement?.type}
              style={[sy.small, sy['text-lightgray'], editable && sy.underline]}
            />
          ) : (
            <Text
              style={[
                sy.small,
                sy['text-lightgray'],
                editable && sy.underline,
              ]}>
              Keuze maken
            </Text>
          )}
        </Pressable>
        <View style={[sy['items-end']]}>
          {overview?.replacement?.type ? (
            <>
              {isFinite(overview?.replacement?.sla?.amount) && (
                <Text style={sy.smallRegular}>
                  {Format.price(overview?.replacement?.sla?.amount)}
                </Text>
              )}
              {(overview?.dealer_request ||
                overview?.fleet_request ||
                overview?.rob) &&
                overview?.replacement?.sla?.rob_code && (
                  <Pressable
                    disabled={is_fleet_car || is_retail_car}
                    onPress={() =>
                      setShowRobReasonsDialog({
                        rob_code: overview?.replacement?.sla?.rob_code,
                      })
                    }>
                    {isRobTaskApproved(
                      Object.values(
                        (
                          overview?.dealer_request ??
                          overview?.fleet_request ??
                          overview?.rob
                        )?.components ?? [],
                      ).find(
                        (item) =>
                          item.rob.code ===
                          overview?.replacement?.sla?.rob_code,
                      )?.status.code,
                    ) ? (
                      <ThumbUpIcon width={18} height={18} fill="#299976" />
                    ) : (
                      <RobTaskIcon
                        statuses={[
                          Object.values(
                            (
                              overview?.dealer_request ??
                              overview?.fleet_request ??
                              overview?.rob
                            )?.components ?? [],
                          ).find(
                            (item) =>
                              item.rob.code ===
                              overview?.replacement?.sla?.rob_code,
                          )?.status.code,
                        ]}
                        width={18}
                        height={18}
                        fill="#972727"
                      />
                    )}
                  </Pressable>
                )}
            </>
          ) : (
            <ListIcon />
          )}
        </View>
      </View>
    </View>
  );
};
const renderRequestAssignmentPanel = ({
  overview,
  total_amount,
  setShowAssignUserDialog,
  components,
  robErrors,
  robContract,
  is_fleet_car,
  is_retail_car,
  slaKpis,
  setShowMaintenanceEdit,
  setShowTiresEdit,
  setShowSlaEdit,
  setShowTaskEdit,
  read_only,
  robComponents,
  editable,
  onActivateComponent,
  user_settings,
  setShowCancelRobTaskDialog,
  onDeleteComponent,
  slaOperations,
  setShowRobTaskErrorsDialog,
  setShowRobReasonsDialog,
  setShowCustomTimeInfoDialog,
  setShowMoreTimeInfoDialog,
  setShowOverlapInfoDialog,
  setShowCustomArticlesInfoDialog,
  setShowDiscountInfoDialog,
  setShowMarkupInfoDialog,
  car,
  setShowReplacementActionsheet,
  setShowDetails,
  request_id,
  navigation,
}) => (
  <View style={[sy['bg-white'], sy['overflow-hidden'], sy['rounded-lg']]}>
    <View style={[sy['flex-row'], sy['justify-between'], sy['p-4']]}>
      <View
        style={[sy['flex-row'], sy['flex-1'], sy['gap-4'], sy['items-center']]}>
        <AssignmentIcon fill="#4A4A49" />
        <View>
          <Text style={sy.mediumPlus}>
            Opdracht&nbsp;&middot;&nbsp;
            {getRequestStatusDescription(overview)}
          </Text>
          <Link
            private
            textStyle={[sy.small, sy['text-lightgray']]}
            onPress={() => setShowAssignUserDialog(true)}>
            {overview?.assigned_user ?? 'Toewijzen'}
          </Link>
        </View>
      </View>
      <Text style={sy.mediumPlus}>{Format.price(total_amount)}</Text>
    </View>
    <Divider style={sy['bg-gray88']} />
    {renderRequestComponentRows({
      components,
      overview,
      robErrors,
      robContract,
      is_fleet_car,
      is_retail_car,
      slaKpis,
      setShowMaintenanceEdit,
      setShowTiresEdit,
      setShowSlaEdit,
      setShowTaskEdit,
      read_only,
      robComponents,
      editable,
      onActivateComponent,
      user_settings,
      setShowCancelRobTaskDialog,
      onDeleteComponent,
      slaOperations,
      setShowRobTaskErrorsDialog,
      setShowRobReasonsDialog,
      setShowCustomTimeInfoDialog,
      setShowMoreTimeInfoDialog,
      setShowOverlapInfoDialog,
      setShowCustomArticlesInfoDialog,
      setShowDiscountInfoDialog,
      setShowMarkupInfoDialog,
      car,
    })}

    {renderRequestReplacementSection({
      overview,
      is_retail_car,
      read_only,
      is_fleet_car,
      robComponents,
      editable,
      setShowReplacementActionsheet,
      setShowRobReasonsDialog,
    })}

    {read_only && (
      <Pressable
        style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}
        onPress={() => setShowDetails(true)}>
        <ReceiptLongIcon />
        <Text style={[sy.underline]}>Details</Text>
      </Pressable>
    )}

    {!is_retail_car && overview?.replacement?.type === REPLACEMENT_TYPE.car && (
      <>
        {read_only && (
          <View style={[sy['px-4']]}>
            <Divider style={sy['bg-gray95']} />
          </View>
        )}
        <ReplacementVehicleRow
          request_id={request_id}
          request_type={overview?.request_type}
          onPress={() =>
            navigation.navigate('ReplacementVehicle', {
              request_id,
            })
          }
        />
      </>
    )}

    {!read_only && (
      <>
        <View style={[sy['px-4']]}>
          <Divider style={sy['bg-gray95']} />
        </View>
        <View
          style={[
            sy['p-4'],
            sy['flex-row'],
            sy['justify-between'],
            sy['items-center'],
            !editable && sy['view-disabled'],
          ]}>
          <Pressable
            style={[sy['flex-row'], sy['gap-4'], sy['items-center']]}
            disabled={!editable}
            onPress={() =>
              navigation.navigate('AddTasks', {
                request_id,
                license: overview?.license,
              })
            }>
            <AddIcon fill="#231fda" />
            <Text>Item toevoegen</Text>
          </Pressable>
          {overview?.request_type === REQUEST_TYPES.ROB_REQUEST &&
            overview?.status === STATUS_CODES.CheckIn &&
            !isNil(overview?.mileage) &&
            String(overview.mileage).trim() !== '' && (
              <ProposalButton request_id={request_id} />
            )}
          {/*is_fleet_car || is_retail_car ? (
            <></>
           ) : (
            <Pressable
              disabled={!editable}
              onPress={() =>
                navigation.navigate('Fource', {
                  request_id,
                  license: overview?.license,
                  dealer_id: overview?.dealer_id,
                })
              }>
              <ShoppingBasketIcon fill="#231fda" />
            </Pressable>
           )*/}
        </View>
      </>
    )}
  </View>
);
const renderRequestCarGrouping = (props) => <VehicleDetails {...props} />;
const renderRequestLessorGrouping = ({
  overview,
  is_retail_car,
  robContract,
  phone_number_missing,
  contract_ends,
  request_id,
  serviceHistory,
  navigation,
  onNewDamageRequest,
  onUpdateRequest,
  is_fleet_car,
  setRobContract,
}) => {
  if (is_retail_car && !overview?.kvk_name) {
    return null;
  }
  let lessorColor = '#4A4A49';
  if (robContract?.isActive === false) {
    lessorColor = '#972727';
  } else if (phone_number_missing) {
    lessorColor = '#FF6C00';
  }
  return (
    <Grouping
      icon={
        <LessorPersona
          lessor={overview?.lessor}
          fallback={<BusinessIcon fill={lessorColor} />}
          size={24}
        />
      }
      header={
        <View style={[sy['flex-row'], sy['items-center']]}>
          <Text style={[sy.mediumPlus, sy.truncate]}>
            {overview?.kvk_name ?? overview?.lessor}
          </Text>
          {contract_ends && (
            <>
              &nbsp;
              <WarningAmberIcon fill="#FF6C00" width={18} height={18} />
            </>
          )}
        </View>
      }>
      {robContract?.lessor?.sublessor && (
        <GroupingRow
          title="Subleasemaatschappij"
          style={[sy['gap-4']]}
          contentStyle={[sy['flex-1']]}>
          <Text style={[sy['text-right']]}>
            {robContract.lessor.sublessor.name}
          </Text>
        </GroupingRow>
      )}
      {robContract?.lessor && (
        <GroupingRow
          title="Telefoonnummer"
          style={[sy['gap-4']]}
          contentStyle={[sy['flex-1']]}>
          <LessorPhone
            lessor={robContract?.lessor}
            style={[sy['text-right']]}
          />
        </GroupingRow>
      )}
      {robContract?.leaseContract?.endDate && (
        <GroupingRow
          title={
            <View style={[sy['flex-row'], sy['items-center']]}>
              <Text>Einde contract</Text>
              {contract_ends && (
                <>
                  &nbsp;
                  <WarningAmberIcon fill="#FF6C00" width={18} height={18} />
                </>
              )}
            </View>
          }>
          <Text>
            {moment(robContract?.leaseContract.endDate).format('D-M-YYYY')}
            &nbsp;(
            {Format.number(
              robContract?.leaseContract.maximumMileage,
              0,
              true,
            )}{' '}
            km)
          </Text>
        </GroupingRow>
      )}
      {is_retail_car ? (
        <></>
      ) : (
        <>
          <GroupingRow
            title={
              <View style={[sy['flex-row'], sy['items-center']]}>
                <Text>Laatste werkopdracht</Text>
              </View>
            }>
            <ServiceHistoryLink
              request_id={request_id}
              license={overview?.license}
              history={serviceHistory}
              dealer_id={overview?.dealer_id}
            />
          </GroupingRow>
          <GroupingRow title="Reparatie & onderhoud">
            <Text>
              {robContract?.permissions.repairAndMaintenance ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Schade">
            {renderDamageRequestLink({
              overview,
              robContract,
              navigation,
              onNewDamageRequest,
            })}
          </GroupingRow>
          <GroupingRow title="Bandvervanging">
            <Text>
              {robContract?.permissions.tires.replace ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Seizoenswissel">
            <Text>
              {robContract?.permissions.tires.swap ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
          <GroupingRow title="Vervangend vervoer">
            <Text>
              {robContract?.permissions.replacement_vehicle?.allowed ? (
                <>Toegestaan</>
              ) : (
                <>Niet toegestaan</>
              )}
            </Text>
          </GroupingRow>
        </>
      )}
      {is_retail_car && overview?.kvk_name && (
        <>
          <GroupingRow title="Bedrijfsnaam">
            <Text>{overview?.kvk_name}</Text>
          </GroupingRow>
          <GroupingRow title="KVK nummer">
            <Text>{overview?.kvk_number}</Text>
          </GroupingRow>
        </>
      )}
      {!is_retail_car && (
        <>
          <ClientFields
            overview={overview}
            phone_number_missing={phone_number_missing}
            onUpdateRequest={onUpdateRequest}
          />
          {!is_fleet_car && robContract?.leaseContract && (
            <GroupingRow>
              <View style={[sy['flex-row'], sy['justify-between']]}>
                {robContract.isActive ? (
                  <Pressable
                    style={[sy['flex-row']]}
                    onPress={async () => {
                      const {result: rob_contract} =
                        await robApi.update_contract(
                          overview.license,
                          request_id,
                        );
                      setRobContract(rob_contract);
                    }}>
                    <View style={[sy['pr-8']]}>
                      <RefreshIcon fill="#231fda" />
                    </View>
                    <View>
                      <Link>Gegevens vernieuwen</Link>
                      <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                        Laatst bijgewerkt&nbsp;
                        {moment(robContract?.timestamp).format(
                          'D MMMM YYYY H:mm',
                        )}
                      </Text>
                    </View>
                  </Pressable>
                ) : (
                  <View style={sy['flex-row']}>
                    <View style={[sy['pr-8']]}>
                      <WarningLargeIcon fill="#972727" />
                    </View>
                    <View>
                      <Text>Contract inactief</Text>
                      <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                        Laatst bijgewerkt&nbsp;
                        {moment(robContract?.timestamp).format(
                          'D MMMM YYYY H:mm',
                        )}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </GroupingRow>
          )}
        </>
      )}
    </Grouping>
  );
};
const renderRequestRetailClientGrouping = ({
  is_retail_car,
  overview,
  phone_number_missing,
  onUpdateRequest,
}) => {
  if (!is_retail_car) {
    return null;
  }
  return (
    <Grouping
      icon={<PersonIcon fill="#4A4A49" />}
      header={
        <PrivateText style={[sy.mediumPlus, sy.truncate]}>
          {(overview.client_first_name || overview.client_initials) &&
          overview.client_last_name
            ? `${overview.client_first_name || overview.client_initials} ${
                overview.client_last_name
              }`
            : 'Berijder'}
        </PrivateText>
      }>
      <ClientFields
        overview={overview}
        phone_number_missing={phone_number_missing}
        onUpdateRequest={onUpdateRequest}
      />
    </Grouping>
  );
};
const RequestEditActionsheets = React.memo(
  ({
    showMaintenanceEdit,
    setShowMaintenanceEdit,
    request_id,
    overview,
    is_fleet_car,
    is_retail_car,
    vin_number,
    car,
    onMaintenanceEdit,
    setShowRobReasonsDialog,
    showTaskEdit,
    setShowTaskEdit,
    onTaskEdit,
    showSlaEdit,
    setShowSlaEdit,
    onSlaEdit,
    showTiresEdit,
    setShowTiresEdit,
    onTiresEdit,
  }) => (
    <>
      {showMaintenanceEdit && (
        <MaintenanceActionsheet
          onDismiss={() => setShowMaintenanceEdit(null)}
          request_id={request_id}
          damage_id={overview.damage_request?.id}
          license={overview.license}
          is_fleet_car={is_fleet_car}
          is_retail_car={is_retail_car}
          dealer_id={overview.dealer_id}
          company_id={overview.company_id}
          lessor={overview.lessor}
          vin_number={vin_number}
          mileage={overview.mileage}
          car={car}
          task={showMaintenanceEdit.task}
          additional={showMaintenanceEdit.additional}
          component={showMaintenanceEdit.component}
          rob={
            (overview.dealer_request ?? overview.fleet_request ?? overview.rob)
              ?.components[showMaintenanceEdit.task.id]
          }
          onChange={onMaintenanceEdit}
          onShowDenialReasons={() =>
            setShowRobReasonsDialog({
              component_id: showMaintenanceEdit.component.id,
            })
          }
        />
      )}

      {showTaskEdit && (
        <TaskActionsheet
          onDismiss={() => setShowTaskEdit(null)}
          license={overview.license}
          is_fleet_car={is_fleet_car}
          is_retail_car={is_retail_car}
          dealer_id={overview.dealer_id}
          company_id={overview.company_id}
          lessor={overview.lessor}
          vin_number={vin_number}
          mileage={overview.mileage}
          tecrmi_enabled={overview.tecrmi_enabled}
          request_id={request_id}
          damage_id={overview.damage_request?.id}
          main_task={showTaskEdit.main_task}
          tasks={showTaskEdit.tasks}
          parts={showTaskEdit.parts}
          component={showTaskEdit.component}
          rob={
            (overview.dealer_request ?? overview.fleet_request ?? overview.rob)
              ?.components[showTaskEdit.main_task.id]
          }
          onChange={onTaskEdit}
          onShowDenialReasons={() =>
            setShowRobReasonsDialog({
              component_id: showTaskEdit.component.id,
            })
          }
        />
      )}

      {showSlaEdit && (
        <SlaActionsheet
          onDismiss={() => setShowSlaEdit(null)}
          license={overview.license}
          is_fleet_car={is_fleet_car}
          is_retail_car={is_retail_car}
          request_id={request_id}
          damage_id={overview.damage_request?.id}
          task_id={showSlaEdit.task.id}
          component={showSlaEdit.component}
          rob={
            (overview.dealer_request ?? overview.fleet_request ?? overview.rob)
              ?.components[showSlaEdit.task.id]
          }
          onChange={onSlaEdit}
          onShowDenialReasons={() =>
            setShowRobReasonsDialog({
              component_id: showSlaEdit.component.id,
            })
          }
        />
      )}

      {showTiresEdit && (
        <TiresActionsheet
          onDismiss={() => setShowTiresEdit(null)}
          license={overview.license}
          is_fleet_car={is_fleet_car}
          is_retail_car={is_retail_car}
          request_id={request_id}
          onChange={onTiresEdit}
        />
      )}
    </>
  ),
);
RequestEditActionsheets.displayName = 'RequestEditActionsheets';

// Memoized: six static info dialogs whose only inputs are visibility booleans
// and their (stable) setters. Each Dialog mounts a Portal even while hidden, so
// keeping them out of RequestTab's render path matters.
const RequestInfoDialogs = React.memo(
  ({
    showCustomTimeInfoDialog,
    setShowCustomTimeInfoDialog,
    showMoreTimeInfoDialog,
    setShowMoreTimeInfoDialog,
    showCustomArticlesInfoDialog,
    setShowCustomArticlesInfoDialog,
    showOverlapInfoDialog,
    setShowOverlapInfoDialog,
    showDiscountInfoDialog,
    setShowDiscountInfoDialog,
    showMarkupInfoDialog,
    setShowMarkupInfoDialog,
  }) => (
    <>
      <Dialog
        visible={showCustomTimeInfoDialog}
        title="Arbeidstijd ingevoerd"
        titleIcon={<TimerIcon fill="#FF6C00" />}
        onDismiss={() => setShowCustomTimeInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            De huidige waarde van de arbeidstijd is handmatig ingevoerd.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showMoreTimeInfoDialog}
        title="Arbeidstijd bijgewerkt"
        titleIcon={<MoreTimeIcon fill="#FF6C00" />}
        onDismiss={() => setShowMoreTimeInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            De arbeidstijd is handmatig aangepast naar een andere waarde.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showCustomArticlesInfoDialog}
        title="Artikel ingevoerd"
        titleIcon={<ProductionQuantityLimitsIcon fill="#FF6C00" />}
        onDismiss={() => setShowCustomArticlesInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            Handmatig ingevoerd(e) artikel(en) aanwezig in activiteit.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showOverlapInfoDialog}
        title="Overlapcorrectie"
        titleIcon={<PublishedWithChangesIcon fill="#299976" />}
        onDismiss={() => setShowOverlapInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>
            Arbeidstijd is bijgewerkt door overlap met andere werkzaamheden in
            werkopdracht.
          </Text>
        </View>
      </Dialog>

      <Dialog
        visible={showDiscountInfoDialog}
        title="Korting toegepast"
        titleIcon={<DiscountIcon fill="#299976" />}
        onDismiss={() => setShowDiscountInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>Lager bedrag door toepassing korting.</Text>
        </View>
      </Dialog>

      <Dialog
        visible={showMarkupInfoDialog}
        title="Toeslag toegepast"
        titleIcon={<SellIcon fill="#FF6C00" />}
        onDismiss={() => setShowMarkupInfoDialog(false)}>
        <View style={[sy['py-4']]}>
          <Text>Hoger bedrag door toepassing toeslag.</Text>
        </View>
      </Dialog>
    </>
  ),
);
RequestInfoDialogs.displayName = 'RequestInfoDialogs';
const RequestTailModals = React.memo(
  ({
    showDealerRequestDialog,
    request_id,
    overview,
    setShowDealerRequestDialog,
    refresh,
    showAssignUserDialog,
    setShowAssignUserDialog,
    onUpdateRequest,
    showInvoicePdfUploadDialog,
    setShowInvoicePdfUploadDialog,
    InvoiceServiceRequest,
    showCreateDamageModal,
    setShowCreateDamageModal,
    robComponents,
    onCreateDamageRequest,
    showReplacementActionsheet,
    setShowReplacementActionsheet,
  }) => (
    <>
      {showDealerRequestDialog && (
        <DealerRequestDialog
          visible={true}
          request_id={request_id}
          email={overview?.email}
          phone_number={overview?.phone_number}
          onDismiss={() => setShowDealerRequestDialog(false)}
          onRefresh={refresh}
        />
      )}

      {showAssignUserDialog && (
        <DealerUserActionsheet
          visible={true}
          dealer_id={overview?.dealer_id}
          assigned_user_id={overview?.assigned_user_id}
          onDismiss={() => setShowAssignUserDialog(false)}
          onOK={async (assigned_user_id) => {
            await onUpdateRequest({
              assigned_user_id,
            });
            setShowAssignUserDialog(false);
            refresh();
          }}
        />
      )}

      {showInvoicePdfUploadDialog && (
        <InvoiceUploadDialog
          visible={true}
          lessor={overview.fleet_administrator ?? overview.lessor}
          onDismiss={() => setShowInvoicePdfUploadDialog(false)}
          onOK={async (data) => {
            const errors = await InvoiceServiceRequest(data);
            if (errors) {
              throw errors;
            } else {
              setShowInvoicePdfUploadDialog(false);
            }
          }}
        />
      )}

      {showCreateDamageModal && (
        <CreateDamageModal
          request_id={request_id}
          license={overview.license}
          lessor={overview.lessor}
          components={overview.components}
          rob_components={robComponents}
          onDismiss={() => setShowCreateDamageModal(false)}
          onCreate={onCreateDamageRequest}
        />
      )}

      {showReplacementActionsheet && (
        <ReplacementActionsheet
          visible={true}
          request_id={request_id}
          license={overview.license}
          fleet_contract_id={overview.fleet_contract_id}
          parent_request_type={overview.request_type}
          type={overview.replacement?.type}
          onDismiss={() => setShowReplacementActionsheet(false)}
          onTypeChanged={() => {
            setShowReplacementActionsheet(false);
            refresh();
          }}
        />
      )}
    </>
  ),
);
RequestTailModals.displayName = 'RequestTailModals';
const RequestDialogCluster = (props) => (
  <RequestDialogs {...props} RobReasonsDialog={RobReasonsDialog} />
);
RequestDialogCluster.displayName = 'RequestDialogCluster';
const RequestWorkflowModals = React.memo(
  ({
    showDetails,
    setShowDetails,
    request_id,
    is_fleet_car,
    is_retail_car,
    overview,
    total_amount,
    showCancelRobTaskDialog,
    refresh,
    setShowCancelRobTaskDialog,
    onDeleteComponent,
    showMaintenanceExtrasDialog,
    setShowMaintenanceExtrasDialog,
    navigation,
  }) => (
    <>
      {showDetails && (
        <Details
          onDismiss={() => setShowDetails(false)}
          request_id={request_id}
          is_fleet_car={is_fleet_car}
          is_retail_car={is_retail_car}
          lessor={overview.lessor}
          license={overview.license}
          data={
            overview.dealer_request ?? overview.fleet_request ?? overview.rob
          }
          tasks={overview.components.flatMap((c) => c.tasks ?? [])}
          replacement_rob_code={overview.replacement?.sla?.rob_code}
          total_amount={total_amount}
        />
      )}

      <CancelRobTaskDialog
        visible={!!showCancelRobTaskDialog}
        onDismiss={async () => {
          await refresh();
          setShowCancelRobTaskDialog(null);
        }}
        onCancel={async () => {
          onDeleteComponent(
            showCancelRobTaskDialog.component_id,
            showCancelRobTaskDialog.id,
          );
          await refresh();
          setShowCancelRobTaskDialog(null);
        }}
      />

      <MaintenanceExtrasDialog
        visible={!!showMaintenanceExtrasDialog}
        onDismiss={() => {
          showMaintenanceExtrasDialog.resolve();
          setShowMaintenanceExtrasDialog(null);
        }}
        onConfirm={() => {
          showMaintenanceExtrasDialog.reject();
          setShowMaintenanceExtrasDialog(null);
          navigation.navigate('AddTasks', {
            request_id,
          });
        }}
      />
    </>
  ),
);
RequestWorkflowModals.displayName = 'RequestWorkflowModals';
const maybeAwaitMaintenanceExtrasDialog = async (
  setShowMaintenanceExtrasDialog,
) => {
  await new Promise((resolve, reject) => {
    setShowMaintenanceExtrasDialog({
      resolve,
      reject,
    });
  });
};
const submitRequestByType = async ({
  is_retail_car,
  is_fleet_car,
  overview,
  request_id,
  refresh,
  dispatch,
  setRobErrors,
  setShowDealerRequestDialog,
  setShowRequestDialog,
}) => {
  if (is_retail_car) {
    setShowDealerRequestDialog(true);
    return;
  }
  if (is_fleet_car && overview.status !== STATUS_CODES.CheckIn) {
    const {success} = await fleetApi.request(request_id);
    if (success) {
      await refresh();
    }
    return;
  }
  if (overview.rob?.status) {
    const {success} = await robApi.update_request(request_id);
    if (success) {
      await refresh();
    } else {
      const {result} = await serviceRequestApi.get(request_id, dispatch);
      setRobErrors(result[0].rob_errors);
    }
    return;
  }
  setShowRequestDialog(true);
};
const hasVisitedAddTasks = (navHistory, request_id) =>
  navHistory.find(
    (item) => item.name === 'AddTasks' && item.params.request_id === request_id,
  );
const runRequestRobFlow = async ({
  partialVin,
  current_mileage,
  overview,
  request_id,
  refresh,
  dispatch,
  setRobErrors,
  setShowRequestDialog,
  setShowRobRequestErrorsDialog,
}) => {
  if (!partialVin || !current_mileage) {
    return;
  }
  const {replacement, ...rest} = overview;
  await serviceRequestApi.patch(request_id, {
    ...rest,
    mileage: current_mileage,
  });
  const {success} = await robApi.request(
    request_id,
    partialVin,
    current_mileage,
  );
  if (success) {
    await refresh();
    setShowRequestDialog(false);
    return;
  }
  const {result} = await serviceRequestApi.get(request_id, dispatch);
  setRobErrors(result[0].rob_errors);
  setShowRequestDialog(false);
  setShowRobRequestErrorsDialog(
    result[0].rob_errors?.filter((item) => !item.task_id)?.length > 0,
  );
};
const invoiceServiceRequestByType = async ({
  overview,
  request_id,
  dispatch,
  refresh,
  setRobErrors,
  setShowRobRequestErrorsDialog,
  pdf,
}) => {
  if (overview?.dealer_request) {
    const {success} = await dealerApi.invoice(request_id);
    if (success) {
      await refresh();
    }
    return null;
  }
  if (overview?.fleet_request) {
    const {success, errors} = await fleetApi.invoice(request_id, pdf);
    if (success) {
      await refresh();
      return null;
    }
    return errors;
  }
  const {success, errors} = await robApi.invoice(
    request_id,
    overview.invoice_number,
  );
  if (success) {
    await serviceRequestApi.change_status(request_id, STATUS_CODES.Invoiced);
    await refresh();
    return null;
  }
  setRobErrors(errors);
  setShowRobRequestErrorsDialog(true);
  return null;
};
const resolveMaintenanceTaskId = ({overview, changes}) => {
  if (changes.task_id) {
    return changes.task_id;
  }
  return overview.components
    .flatMap((c) => c.tasks ?? [])
    .find(
      (item) =>
        item.rob_code === changes.rob_code &&
        item.is_main_task &&
        item.type === 'MAINTENANCE',
    )?.id;
};
const buildSlaTaskUpdatePayload = ({overview, task_id, changes}) => {
  const {parts, warranty, leniency_amount, is_damage, discount, markup} =
    changes;
  const existing_task = overview.components
    .flatMap((c) => c.tasks ?? [])
    .find((item) => item.id === task_id);
  return {
    ...existing_task,
    warranty,
    leniency_amount,
    is_damage,
    discount,
    markup,
    parts,
  };
};
const runWithAppLoading = async ({dispatch, task, onFinally}) => {
  dispatch(setAppIsLoading());
  try {
    return await task();
  } catch (e) {
    console.error(e);
  } finally {
    if (onFinally) {
      await onFinally();
    }
    dispatch(setAppDoneLoading());
  }
};
const resolveRequestMainTask = ({overview, main_task_id, rob_code}) => {
  let main_task = overview.components
    .flatMap((c) => c.tasks ?? [])
    .find((item) => item.id === main_task_id && item.is_main_task);
  if (!main_task) {
    main_task = overview.components
      .flatMap((c) => c.tasks ?? [])
      .find((item) => item.rob_code === rob_code && item.is_main_task);
  }
  return main_task;
};
const buildRequestTaskChangesPayload = ({
  overview,
  changes,
  is_fleet_car,
  is_retail_car,
}) => {
  const {
    main_task_id,
    component_id,
    rob_code,
    operation,
    reason,
    location,
    parts,
    warranty,
    leniency_amount,
    is_damage,
    tasks: changed_tasks,
  } = changes;
  const main_task = resolveRequestMainTask({
    overview,
    main_task_id,
    rob_code,
  });
  if (main_task?.id !== main_task_id) {
    return {
      shouldCloseOnly: true,
    };
  }
  const tasks = changed_tasks.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
  const changed_task = changed_tasks.find((t) => t.id === main_task.id);
  tasks[main_task.id] = {
    ...tasks[main_task.id],
    ...changed_task,
    id: main_task.id,
    component_id,
    rob_code,
    operation,
    reason,
    location,
    parts,
    warranty,
    leniency_amount,
    is_damage,
  };
  if (!is_fleet_car && !is_retail_car) {
    for (const child of changed_tasks.filter((t) => t.id !== main_task.id)) {
      tasks[child.id] = {
        ...child,
        component_id,
        rob_code,
        operation,
        reason,
        location,
        warranty: null,
        leniency_amount: null,
        is_damage: null,
        parts: null,
      };
    }
  }
  return {
    shouldCloseOnly: false,
    tasks: Object.values(tasks).filter(Boolean),
  };
};
const createRequestSubmissionActions = ({
  is_retail_car,
  is_fleet_car,
  overview,
  request_id,
  refresh,
  dispatch,
  setRobErrors,
  setShowDealerRequestDialog,
  setShowRequestDialog,
  user_settings,
  navHistory,
  setShowMaintenanceExtrasDialog,
  partialVin,
  setPartialVinError,
  current_mileage,
  setShowRobRequestErrorsDialog,
  invoice,
  setShowInvoicePdfUploadDialog,
  onUpdateRequest,
  invoiceStatus,
  setShowNoInvoiceDialog,
  setShowNoInvoiceInfoDialog,
}) => {
  const submitRequest = async () =>
    submitRequestByType({
      is_retail_car,
      is_fleet_car,
      overview,
      request_id,
      refresh,
      dispatch,
      setRobErrors,
      setShowDealerRequestDialog,
      setShowRequestDialog,
    });
  const hasSeenAddTasks = () => hasVisitedAddTasks(navHistory, request_id);
  const onDoRequest = async () => {
    if (
      shouldShowMaintenanceExtrasDialog({
        is_retail_car,
        is_fleet_car,
        overview,
        user_settings,
        hasSeenAddTasks: hasSeenAddTasks(),
      })
    ) {
      try {
        await maybeAwaitMaintenanceExtrasDialog(setShowMaintenanceExtrasDialog);
      } catch {
        return;
      }
    }
    await runWithAppLoading({
      dispatch,
      task: async () => {
        await submitRequest();
      },
    });
  };
  const onRequestROB = async () => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        await runRequestRobFlow({
          partialVin,
          current_mileage,
          overview,
          request_id,
          refresh,
          dispatch,
          setRobErrors,
          setShowRequestDialog,
          setShowRobRequestErrorsDialog,
        });
      },
    });
  };
  const onRequest = async () => {
    if (is_fleet_car) {
      const {success, error} = await fleetApi.request(
        request_id,
        partialVin,
        current_mileage,
      );
      setPartialVinError(error === 'PARTIAL_VIN_MISMATCH');
      if (success) {
        setShowRequestDialog(false);
        await refresh();
      }
      return;
    }
    await onRequestROB();
  };
  const InvoiceServiceRequest = async (pdf) => {
    return runWithAppLoading({
      dispatch,
      task: async () =>
        await invoiceServiceRequestByType({
          overview,
          request_id,
          dispatch,
          refresh,
          setRobErrors,
          setShowRobRequestErrorsDialog,
          pdf,
        }),
    });
  };
  const onInvoice = async () => {
    if (invoice?.pdf_upload && !invoice?.bahnkick_can_invoice) {
      setShowInvoicePdfUploadDialog(true);
    } else {
      await InvoiceServiceRequest();
    }
  };
  const onChangeInvoiceStatus = async () => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        if (overview?.invoice_number) {
          // Clear the entered invoice number
          await onUpdateRequest({
            invoice_number: null,
          });
        }
        const {success} = await robApi.change_invoice_status(
          request_id,
          invoiceStatus,
        );
        if (success) {
          setShowNoInvoiceDialog(false);
          setShowNoInvoiceInfoDialog(true);
        }
      },
      onFinally: refresh,
    });
  };
  return {
    onDoRequest,
    onRequest,
    onRequestROB,
    InvoiceServiceRequest,
    onInvoice,
    onChangeInvoiceStatus,
  };
};
const createRequestEditActions = ({
  dispatch,
  request_id,
  refresh,
  setRobErrors,
  overview,
  is_fleet_car,
  is_retail_car,
  showMaintenanceEdit,
  setShowMaintenanceEdit,
  setShowTaskEdit,
  showSlaEdit,
  setShowSlaEdit,
  setShowTiresEdit,
  setShowCreateDamageModal,
  navigation,
  car,
}) => {
  const onActivateComponent = async (component_id, main_task_id) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        const {success, errors} = await serviceRequestApi.activate_component(
          request_id,
          component_id,
          main_task_id,
        );
        if (errors?.length > 0) {
          setRobErrors(errors);
        }
        if (success) {
          await refresh();
        }
      },
    });
  };
  const onDeleteComponent = async (component_id, main_task_id) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        await serviceRequestApi.delete_component(
          request_id,
          component_id,
          main_task_id,
        );
      },
      onFinally: refresh,
    });
  };
  const onUpdateCar = async (attributes) => {
    const result = await carApi.change_attributes(car.license, attributes);
    if (result?.success) {
      refresh();
    }
  };
  const onMaintenanceEdit = async (changes) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        const task_id = resolveMaintenanceTaskId({
          overview,
          changes,
        });
        const {id: component_id} = showMaintenanceEdit.component;
        const result = await serviceRequestApi.update_maintenance_task(
          request_id,
          {
            ...changes,
            task_id,
            component_id: changes.component_id ?? component_id,
          },
        );
        if (result.success) {
          setShowMaintenanceEdit(null);
        }
      },
      onFinally: () => refresh(REQUEST_EDIT_SCOPES),
    });
  };
  const onTaskEdit = async (changes) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        const payload = buildRequestTaskChangesPayload({
          overview,
          changes,
          is_fleet_car,
          is_retail_car,
        });
        if (payload.shouldCloseOnly) {
          refresh(REQUEST_EDIT_SCOPES);
          setShowTaskEdit(null);
          return;
        }
        const result = await serviceRequestApi.update_tasks(request_id, {
          tasks: payload.tasks,
        });
        if (result.success) {
          refresh(REQUEST_EDIT_SCOPES);
          setShowTaskEdit(null);
        }
      },
    });
  };
  const onSlaEdit = async (changes) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        const {id: task_id} = showSlaEdit.task;
        const result = await serviceRequestApi.update_tasks(request_id, {
          tasks: [
            buildSlaTaskUpdatePayload({
              overview,
              task_id,
              changes,
            }),
          ],
        });
        if (result.success) {
          refresh(REQUEST_EDIT_SCOPES);
          setShowSlaEdit(null);
        }
      },
    });
  };
  const onTiresEdit = async (changes) => {
    await runWithAppLoading({
      dispatch,
      task: async () => {
        const result = await serviceRequestApi.update_tires(request_id, {
          ...changes,
        });
        if (result.success) {
          setShowTiresEdit(null);
          refresh(REQUEST_EDIT_SCOPES);
        }
      },
    });
  };
  const onCreateDamageRequest = async (components) => {
    try {
      const {success, damage_id} = await serviceRequestApi.damage_request(
        request_id,
        components,
      );
      if (success) {
        setShowCreateDamageModal(false);
        await refresh();
        navigation.navigate('Damage', {
          damage_id,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };
  const onNewDamageRequest = async () => {
    if (overview.components.length > 0) {
      setShowCreateDamageModal(true);
      return;
    }
    await onCreateDamageRequest([]);
  };
  return {
    onActivateComponent,
    onDeleteComponent,
    onUpdateCar,
    onMaintenanceEdit,
    onTaskEdit,
    onSlaEdit,
    onTiresEdit,
    onNewDamageRequest,
    onCreateDamageRequest,
  };
};
const RequestTab = withDimensions(({dimensions, focused, onChangeTab}) => {
  const navigation = useNavigation();
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [partialVin, setPartialVin] = useState(null);
  const [partialVinError, setPartialVinError] = useState(false);
  const [editInvoiceNumber, setEditInvoiceNumber] = useState(null);
  const [editVin, setEditVin] = useState({});
  const [editDateTime, setEditDateTime] = useState(null);
  const [editKilometrage, setEditKilometrage] = useState(null);
  const [showMaintenanceEdit, setShowMaintenanceEdit] = useState(null);
  const [showTaskEdit, setShowTaskEdit] = useState(null);
  const [showSlaEdit, setShowSlaEdit] = useState(null);
  const [showTiresEdit, setShowTiresEdit] = useState(null);
  const [showRobReasonsDialog, setShowRobReasonsDialog] = useState(null);
  const [showRobTaskErrorsDialog, setShowRobTaskErrorsDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCancelRobTaskDialog, setShowCancelRobTaskDialog] = useState(null);
  const [showCustomTimeInfoDialog, setShowCustomTimeInfoDialog] =
    useState(false);
  const [showMoreTimeInfoDialog, setShowMoreTimeInfoDialog] = useState(false);
  const [showCustomArticlesInfoDialog, setShowCustomArticlesInfoDialog] =
    useState(false);
  const [showOverlapInfoDialog, setShowOverlapInfoDialog] = useState(false);
  const [showAssignUserDialog, setShowAssignUserDialog] = useState(false);
  const [showDiscountInfoDialog, setShowDiscountInfoDialog] = useState(false);
  const [showMarkupInfoDialog, setShowMarkupInfoDialog] = useState(false);
  const [showNoInvoiceDialog, setShowNoInvoiceDialog] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [showNoInvoiceInfoDialog, setShowNoInvoiceInfoDialog] = useState(false);
  const [showRobRequestErrorsDialog, setShowRobRequestErrorsDialog] =
    useState(false);
  const [showMaintenanceExtrasDialog, setShowMaintenanceExtrasDialog] =
    useState(null);
  const [showDealerRequestDialog, setShowDealerRequestDialog] = useState(false);
  const [showInvoicePdfUploadDialog, setShowInvoicePdfUploadDialog] =
    useState(false);
  const [showCreateDamageModal, setShowCreateDamageModal] = useState(false);
  const [showReplacementActionsheet, setShowReplacementActionsheet] =
    useState(false);
  const dispatch = useDispatch();
  const navHistory = useSelector((state) => state.nav_history);
  const {
    request_id,
    overview,
    editable,
    read_only,
    car,
    current_mileage,
    robComponents,
    invoice,
    vin_part,
    vin_number,
    robContract,
    slaKpis,
    slaOperations,
    is_fleet_car,
    is_retail_car,
    robErrors,
    serviceHistory,
    refresh,
    setCar,
    setCurrentMileage,
    setRobContract,
    setRobErrors,
  } = useContext(ServiceRequestContext);
  const user_settings = useSelector((state) => state.user_settings);
  useEffect(() => {
    if (focused) {
      refresh();
    }
  }, [focused, refresh]);
  useEffect(() => {
    // Keep partial vin in sync with vin_part.
    setPartialVin(vin_part);
  }, [vin_part]);
  const onUpdateRequest = useCallback(
    async (changes) => {
      const {replacement, ...rest} = overview;
      const result = await serviceRequestApi.patch(request_id, {
        ...rest,
        ...changes,
      });
      if (result?.success) {
        refresh();
      }
      return result?.success;
    },
    [overview, request_id, refresh],
  );
  const {
    onDoRequest,
    onRequest,
    InvoiceServiceRequest,
    onInvoice,
    onChangeInvoiceStatus,
  } = useMemo(
    () =>
      createRequestSubmissionActions({
        is_retail_car,
        is_fleet_car,
        overview,
        request_id,
        refresh,
        dispatch,
        setRobErrors,
        setShowDealerRequestDialog,
        setShowRequestDialog,
        user_settings,
        navHistory,
        setShowMaintenanceExtrasDialog,
        partialVin,
        setPartialVinError,
        current_mileage,
        setShowRobRequestErrorsDialog,
        invoice,
        setShowInvoicePdfUploadDialog,
        onUpdateRequest,
        invoiceStatus,
        setShowNoInvoiceDialog,
        setShowNoInvoiceInfoDialog,
      }),
    // Deps mirror the argument list exactly, so a handler can never close
    // over a stale value.
    [
      is_retail_car,
      is_fleet_car,
      overview,
      request_id,
      refresh,
      dispatch,
      setRobErrors,
      setShowDealerRequestDialog,
      setShowRequestDialog,
      user_settings,
      navHistory,
      setShowMaintenanceExtrasDialog,
      partialVin,
      setPartialVinError,
      current_mileage,
      setShowRobRequestErrorsDialog,
      invoice,
      setShowInvoicePdfUploadDialog,
      onUpdateRequest,
      invoiceStatus,
      setShowNoInvoiceDialog,
      setShowNoInvoiceInfoDialog,
    ],
  );
  const {
    onActivateComponent,
    onDeleteComponent,
    onUpdateCar,
    onMaintenanceEdit,
    onTaskEdit,
    onSlaEdit,
    onTiresEdit,
    onNewDamageRequest,
    onCreateDamageRequest,
  } = useMemo(
    () =>
      createRequestEditActions({
        dispatch,
        request_id,
        refresh,
        setRobErrors,
        overview,
        is_fleet_car,
        is_retail_car,
        showMaintenanceEdit,
        setShowMaintenanceEdit,
        setShowTaskEdit,
        showSlaEdit,
        setShowSlaEdit,
        setShowTiresEdit,
        setShowCreateDamageModal,
        navigation,
        car,
      }),
    // Deps mirror the argument list exactly, so a handler can never close
    // over a stale value.
    [
      dispatch,
      request_id,
      refresh,
      setRobErrors,
      overview,
      is_fleet_car,
      is_retail_car,
      showMaintenanceEdit,
      setShowMaintenanceEdit,
      setShowTaskEdit,
      showSlaEdit,
      setShowSlaEdit,
      setShowTiresEdit,
      setShowCreateDamageModal,
      navigation,
      car,
    ],
  );
  const components =
    overview?.components
      .map((item) => item.id)
      .sort(compareComponentIds(overview?.components ?? [], is_fleet_car)) ??
    [];
  const contract_ends =
    robContract?.leaseContract?.endDate &&
    moment(robContract.leaseContract.endDate).isBefore(
      moment().add(3, 'months'),
    );
  const phone_number_missing =
    overview?.replacement?.type === REPLACEMENT_TYPE.message &&
    !overview?.phone_number;
  let total_amount = 0;
  if (overview?.status !== STATUS_CODES.Cancelled) {
    total_amount = overview?.components
      .flatMap((c) => c.tasks ?? [])
      ?.filter((task) =>
        shouldIncludeTaskAmount({
          task,
          overview,
          is_fleet_car,
          is_retail_car,
        }),
      )
      .reduce((acc, task) => acc + task.task_amount, 0);
    total_amount += overview?.replacement?.sla?.amount ?? 0;
  }
  if (!overview) {
    return <></>;
  }
  return (
    <>
      <ScrollView
        style={[sy['bg-gray95']]}
        contentContainerStyle={[sy['p-2'], sy['gap-2'], sy['mb-14']]}>
        {renderRequestScheduleGrouping({
          overview,
          editable,
          is_fleet_car,
          editDateTime,
          setEditDateTime,
          onUpdateRequest,
        })}

        {renderRequestCarGrouping({
          overview,
          car,
          vin_number,
          vin_part,
          editVin,
          setEditVin,
          onUpdateRequest,
          refresh,
          editKilometrage,
          setEditKilometrage,
          setCar,
          onUpdateCar,
        })}
        {renderRequestLessorGrouping({
          overview,
          is_retail_car,
          robContract,
          phone_number_missing,
          contract_ends,
          request_id,
          serviceHistory,
          navigation,
          onNewDamageRequest,
          onUpdateRequest,
          is_fleet_car,
          setRobContract,
        })}
        {renderRequestRetailClientGrouping({
          is_retail_car,
          overview,
          phone_number_missing,
          onUpdateRequest,
        })}
        {renderRequestAssignmentPanel({
          overview,
          total_amount,
          setShowAssignUserDialog,
          components,
          robErrors,
          robContract,
          is_fleet_car,
          is_retail_car,
          slaKpis,
          setShowMaintenanceEdit,
          setShowTiresEdit,
          setShowSlaEdit,
          setShowTaskEdit,
          read_only,
          robComponents,
          editable,
          onActivateComponent,
          user_settings,
          setShowCancelRobTaskDialog,
          onDeleteComponent,
          slaOperations,
          setShowRobTaskErrorsDialog,
          setShowRobReasonsDialog,
          setShowCustomTimeInfoDialog,
          setShowMoreTimeInfoDialog,
          setShowOverlapInfoDialog,
          setShowCustomArticlesInfoDialog,
          setShowDiscountInfoDialog,
          setShowMarkupInfoDialog,
          car,
          setShowReplacementActionsheet,
          setShowDetails,
          request_id,
          navigation,
        })}

        {renderRequestInvoicePanel({
          overview,
          is_fleet_car,
          is_retail_car,
          invoice,
          total_amount,
          editInvoiceNumber,
          setEditInvoiceNumber,
          onUpdateRequest,
          dimensions,
          request_id,
          setShowNoInvoiceDialog,
          onDoRequest,
        })}
      </ScrollView>

      <Footer
        request_id={request_id}
        is_fleet_car={is_fleet_car}
        is_retail_car={is_retail_car}
        editable={editable}
        status={overview.status}
        overview={overview}
        robContract={robContract}
        invoice={invoice}
        amount={total_amount}
        robErrors={robErrors}
        onDoRequest={onDoRequest}
        onRefresh={refresh}
        onInvoice={onInvoice}
        onShowRobRequestErrors={() => setShowRobRequestErrorsDialog(true)}
        onChangeTab={onChangeTab}
      />

      <RequestEditActionsheets
        showMaintenanceEdit={showMaintenanceEdit}
        setShowMaintenanceEdit={setShowMaintenanceEdit}
        request_id={request_id}
        overview={overview}
        is_fleet_car={is_fleet_car}
        is_retail_car={is_retail_car}
        vin_number={vin_number}
        car={car}
        onMaintenanceEdit={onMaintenanceEdit}
        setShowRobReasonsDialog={setShowRobReasonsDialog}
        showTaskEdit={showTaskEdit}
        setShowTaskEdit={setShowTaskEdit}
        onTaskEdit={onTaskEdit}
        showSlaEdit={showSlaEdit}
        setShowSlaEdit={setShowSlaEdit}
        onSlaEdit={onSlaEdit}
        showTiresEdit={showTiresEdit}
        setShowTiresEdit={setShowTiresEdit}
        onTiresEdit={onTiresEdit}
      />

      <RequestDialogCluster
        showRequestDialog={showRequestDialog}
        is_fleet_car={is_fleet_car}
        is_retail_car={is_retail_car}
        setShowRequestDialog={setShowRequestDialog}
        onRequest={onRequest}
        partialVin={partialVin}
        setPartialVin={setPartialVin}
        partialVinError={partialVinError}
        setPartialVinError={setPartialVinError}
        current_mileage={current_mileage}
        setCurrentMileage={setCurrentMileage}
        showRobReasonsDialog={showRobReasonsDialog}
        overview={overview}
        robComponents={robComponents}
        setShowRobReasonsDialog={setShowRobReasonsDialog}
        showNoInvoiceDialog={showNoInvoiceDialog}
        setShowNoInvoiceDialog={setShowNoInvoiceDialog}
        onChangeInvoiceStatus={onChangeInvoiceStatus}
        setInvoiceStatus={setInvoiceStatus}
        invoiceStatus={invoiceStatus}
        showNoInvoiceInfoDialog={showNoInvoiceInfoDialog}
        setShowNoInvoiceInfoDialog={setShowNoInvoiceInfoDialog}
        showRobRequestErrorsDialog={showRobRequestErrorsDialog}
        robErrors={robErrors}
        setShowRobRequestErrorsDialog={setShowRobRequestErrorsDialog}
        showRobTaskErrorsDialog={showRobTaskErrorsDialog}
        setShowRobTaskErrorsDialog={setShowRobTaskErrorsDialog}
      />
      <RequestWorkflowModals
        showDetails={showDetails}
        setShowDetails={setShowDetails}
        request_id={request_id}
        is_fleet_car={is_fleet_car}
        is_retail_car={is_retail_car}
        overview={overview}
        total_amount={total_amount}
        showCancelRobTaskDialog={showCancelRobTaskDialog}
        refresh={refresh}
        setShowCancelRobTaskDialog={setShowCancelRobTaskDialog}
        onDeleteComponent={onDeleteComponent}
        showMaintenanceExtrasDialog={showMaintenanceExtrasDialog}
        setShowMaintenanceExtrasDialog={setShowMaintenanceExtrasDialog}
        navigation={navigation}
      />

      <RequestInfoDialogs
        showCustomTimeInfoDialog={showCustomTimeInfoDialog}
        setShowCustomTimeInfoDialog={setShowCustomTimeInfoDialog}
        showMoreTimeInfoDialog={showMoreTimeInfoDialog}
        setShowMoreTimeInfoDialog={setShowMoreTimeInfoDialog}
        showCustomArticlesInfoDialog={showCustomArticlesInfoDialog}
        setShowCustomArticlesInfoDialog={setShowCustomArticlesInfoDialog}
        showOverlapInfoDialog={showOverlapInfoDialog}
        setShowOverlapInfoDialog={setShowOverlapInfoDialog}
        showDiscountInfoDialog={showDiscountInfoDialog}
        setShowDiscountInfoDialog={setShowDiscountInfoDialog}
        showMarkupInfoDialog={showMarkupInfoDialog}
        setShowMarkupInfoDialog={setShowMarkupInfoDialog}
      />

      <RequestTailModals
        showDealerRequestDialog={showDealerRequestDialog}
        request_id={request_id}
        overview={overview}
        setShowDealerRequestDialog={setShowDealerRequestDialog}
        refresh={refresh}
        showAssignUserDialog={showAssignUserDialog}
        setShowAssignUserDialog={setShowAssignUserDialog}
        onUpdateRequest={onUpdateRequest}
        showInvoicePdfUploadDialog={showInvoicePdfUploadDialog}
        setShowInvoicePdfUploadDialog={setShowInvoicePdfUploadDialog}
        InvoiceServiceRequest={InvoiceServiceRequest}
        showCreateDamageModal={showCreateDamageModal}
        setShowCreateDamageModal={setShowCreateDamageModal}
        robComponents={robComponents}
        onCreateDamageRequest={onCreateDamageRequest}
        showReplacementActionsheet={showReplacementActionsheet}
        setShowReplacementActionsheet={setShowReplacementActionsheet}
      />
    </>
  );
});
export default RequestTab;
